import React, { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Utensils,
  Clock,
  Star,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getUserMealsAction } from "../../../store/slices/meals/mealsSliceActions";
import { API_STATUS } from "../../../constants/apiStatus";
import { colors } from "../../../constants/colors";

const MealsSection = ({ uid }) => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Today's date in YYYY-MM-DD format
  const [showMeals, setShowMeals] = useState(false);

  const dispatch = useAppDispatch();
  const { userMeals, apiStatus, error } = useAppSelector(
    (state) => state.meals
  );

  const isLoadingMeals = apiStatus.getUserMeals === API_STATUS.STARTED;

  // Load user meals when toggled on, uid or date changes
  useEffect(() => {
    if (uid && showMeals) {
      dispatch(getUserMealsAction({ uid, date: selectedDate }));
    }
  }, [uid, showMeals, selectedDate, dispatch]);

  // Fetch meals for specific date
  const fetchMeals = (date) => {
    setSelectedDate(date);
    if (showMeals) {
      dispatch(getUserMealsAction({ uid, date }));
    }
  };

  // Handle meals toggle
  const handleMealsToggle = () => {
    if (!showMeals && userMeals.length === 0) {
      fetchMeals(selectedDate);
    }
    setShowMeals(!showMeals);
  };

  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (showMeals) {
      dispatch(getUserMealsAction({ uid, date }));
    }
  };

  // Format creation date for formats like 2025-08-30_14:41 or timestamp objects
  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return "Unknown";
    try {
      if (typeof createdAt === "string" && createdAt.includes("_")) {
        const [d, t] = createdAt.split("_");
        return new Date(`${d}T${t}:00`).toLocaleString();
      }
      if (createdAt.seconds) {
        return new Date(createdAt.seconds * 1000).toLocaleString();
      }
      return new Date(createdAt).toLocaleString();
    } catch (err) {
      console.error("Error formatting date:", err);
      return createdAt;
    }
  };

  // Safely format an ingredient which can be a string or an object
  // Supports shapes like { name, amount, unit } or { name, quantity }
  const formatIngredient = (ing) => {
    if (ing == null) return "";
    if (typeof ing === "string") return ing;
    if (typeof ing === "number") return String(ing);
    if (typeof ing === "object") {
      const name = ing.name || ing.ingredient || ing.title || "";
      const amount =
        ing.amount !== undefined
          ? ing.amount
          : ing.quantity !== undefined
          ? ing.quantity
          : ing.value !== undefined
          ? ing.value
          : "";
      const unit = ing.unit || ing.units || ing.measure || "";
      const details = [amount, unit].filter(Boolean).join(" ");
      if (name && details) return `${name} - ${details}`;
      if (name) return name;
      if (details) return details;
      // Fallback to JSON to avoid React rendering errors while still being informative
      try {
        return JSON.stringify(ing);
      } catch (e) {
        return "[ingredient]";
      }
    }
    return "";
  };

  // Styles
  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "0.5rem 0",
  };

  const titleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkBlue,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const mealCardStyle = {
    backgroundColor: colors.extraLightGray,
    borderRadius: "12px",
    padding: "1rem",
    marginBottom: "1rem",
    border: `1px solid ${colors.doubleExtraLightGray}`,
  };

  const nutritionGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "0.5rem",
    marginTop: "1rem",
  };

  const nutritionItemStyle = {
    textAlign: "center",
    padding: "0.5rem",
    backgroundColor: colors.white,
    borderRadius: "6px",
    border: `1px solid ${colors.doubleExtraLightGray}`,
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle} onClick={handleMealsToggle}>
        <div style={titleStyle}>
          <Utensils size={20} />
          Meals
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {showMeals ? (
            <EyeOff size={16} color={colors.darkGray} />
          ) : (
            <Eye size={16} color={colors.darkGray} />
          )}
          {showMeals ? (
            <ChevronUp size={20} color={colors.darkGray} />
          ) : (
            <ChevronDown size={20} color={colors.darkGray} />
          )}
          {isLoadingMeals && (
            <RefreshCw
              size={16}
              color={colors.darkGray}
              className="animate-spin"
            />
          )}
        </div>
      </div>

      {showMeals && (
        <div style={{ marginTop: "1rem" }}>
          {/* Date Picker */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "600",
                color: colors.darkBlue,
              }}
            >
              Select Date:
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              style={{
                padding: "0.75rem",
                border: `1px solid ${colors.lightGray}`,
                borderRadius: "8px",
                fontSize: "1rem",
                backgroundColor: colors.white,
                color: colors.darkBlue,
                cursor: "pointer",
                width: "200px",
              }}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}

          {/* Loading State */}
          {isLoadingMeals && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: colors.darkGray,
              }}
            >
              Loading meals for {selectedDate}...
            </div>
          )}

          {/* Meals Display */}
          {!isLoadingMeals && userMeals.length > 0 ? (
            <div>
              <h4
                style={{
                  margin: "0 0 1rem 0",
                  color: colors.darkBlue,
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Meals for {selectedDate} ({userMeals.length} found)
              </h4>

              {userMeals.map((meal, index) => {
                const nutrition = meal.nutrition || {};
                const image = meal.image || meal.imageUrl;
                const mealName =
                  meal.mealName || meal.name || meal.type || "Meal";
                const typeLabel = meal.type
                  ? meal.type.charAt(0).toUpperCase() + meal.type.slice(1)
                  : null;
                return (
                  <div
                    key={meal.id || meal.mealID || index}
                    style={mealCardStyle}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "1rem",
                      }}
                    >
                      <div>
                        <h5
                          style={{
                            margin: "0 0 0.5rem 0",
                            color: colors.darkBlue,
                            fontSize: "1.1rem",
                            fontWeight: "600",
                          }}
                        >
                          {mealName}
                        </h5>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            fontSize: "0.875rem",
                            color: colors.darkGray,
                            flexWrap: "wrap",
                          }}
                        >
                          <Clock size={14} />
                          {formatCreatedAt(meal.createdAt)}
                          {typeLabel && (
                            <span
                              style={{
                                background: colors.white,
                                border: `1px solid ${colors.lightGray}`,
                                padding: "2px 6px",
                                borderRadius: 12,
                                fontSize: "0.7rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              {typeLabel}
                            </span>
                          )}
                          {nutrition.mealScore !== undefined && (
                            <span
                              style={{
                                background: "#fdf4ff",
                                color: "#86198f",
                                padding: "2px 6px",
                                borderRadius: 12,
                                fontSize: "0.7rem",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <Star size={12} /> Score {nutrition.mealScore}
                            </span>
                          )}
                        </div>
                      </div>

                      {meal.rating && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            backgroundColor: "#fbbf24",
                            color: "#92400e",
                            padding: "0.25rem 0.5rem",
                            borderRadius: "12px",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                          }}
                        >
                          <Star size={12} />
                          {meal.rating}
                        </div>
                      )}
                    </div>

                    {/* Meal Image */}
                    {image && (
                      <div style={{ marginBottom: "1rem" }}>
                        <img
                          src={image}
                          alt={mealName}
                          style={{
                            width: "100%",
                            maxWidth: "300px",
                            height: "200px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: `1px solid ${colors.lightGray}`,
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}

                    {/* Description */}
                    {(meal.description || meal.note) && (
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: colors.darkGray,
                          marginBottom: "1rem",
                          fontStyle: "italic",
                        }}
                      >
                        {meal.description || meal.note}
                      </div>
                    )}

                    {Array.isArray(meal.ingredients) &&
                      meal.ingredients.length > 0 && (
                        <div style={{ marginBottom: "1rem" }}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              color: colors.darkGray,
                              marginBottom: 4,
                            }}
                          >
                            Ingredients
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                            }}
                          >
                            {meal.ingredients.map((ing, i) => {
                              const label = formatIngredient(ing);
                              return (
                                <span
                                  key={i}
                                  style={{
                                    background: colors.white,
                                    border: `1px solid ${colors.doubleExtraLightGray}`,
                                    padding: "4px 8px",
                                    borderRadius: 16,
                                    fontSize: "0.65rem",
                                  }}
                                  title={
                                    typeof ing === "object"
                                      ? JSON.stringify(ing)
                                      : undefined
                                  }
                                >
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                    {/* Nutrition Information */}
                    {nutrition && Object.keys(nutrition).length > 0 && (
                      <div style={nutritionGridStyle}>
                        <div style={nutritionItemStyle}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: colors.darkGray,
                            }}
                          >
                            Calories
                          </div>
                          <div style={{ fontWeight: "600", color: "#f59e0b" }}>
                            {nutrition.calories ?? nutrition.energy ?? 0}
                          </div>
                        </div>
                        <div style={nutritionItemStyle}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: colors.darkGray,
                            }}
                          >
                            Protein
                          </div>
                          <div style={{ fontWeight: "600", color: "#3b82f6" }}>
                            {nutrition.protein ?? 0}g
                          </div>
                        </div>
                        <div style={nutritionItemStyle}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: colors.darkGray,
                            }}
                          >
                            Carbs
                          </div>
                          <div style={{ fontWeight: "600", color: "#10b981" }}>
                            {nutrition.carbs ?? nutrition.carbohydrates ?? 0}g
                          </div>
                        </div>
                        <div style={nutritionItemStyle}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: colors.darkGray,
                            }}
                          >
                            Fat
                          </div>
                          <div style={{ fontWeight: "600", color: "#ef4444" }}>
                            {nutrition.fat ?? 0}g
                          </div>
                        </div>
                        <div style={nutritionItemStyle}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: colors.darkGray,
                            }}
                          >
                            Fiber
                          </div>
                          <div style={{ fontWeight: "600", color: "#10b981" }}>
                            {nutrition.fiber ?? 0}g
                          </div>
                        </div>
                        <div style={nutritionItemStyle}>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: colors.darkGray,
                            }}
                          >
                            Sodium
                          </div>
                          <div style={{ fontWeight: "600", color: "#8b5cf6" }}>
                            {nutrition.sodium ?? 0}mg
                          </div>
                        </div>
                        {nutrition.sugars !== undefined && (
                          <div style={nutritionItemStyle}>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: colors.darkGray,
                              }}
                            >
                              Sugars
                            </div>
                            <div style={{ fontWeight: 600 }}>
                              {nutrition.sugars}g
                            </div>
                          </div>
                        )}
                        {nutrition.cholesterol !== undefined && (
                          <div style={nutritionItemStyle}>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: colors.darkGray,
                              }}
                            >
                              Cholesterol
                            </div>
                            <div style={{ fontWeight: 600 }}>
                              {nutrition.cholesterol}mg
                            </div>
                          </div>
                        )}
                        {nutrition.potassium !== undefined && (
                          <div style={nutritionItemStyle}>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: colors.darkGray,
                              }}
                            >
                              Potassium
                            </div>
                            <div style={{ fontWeight: 600 }}>
                              {nutrition.potassium}mg
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            !isLoadingMeals && (
              <div
                style={{
                  padding: "3rem",
                  textAlign: "center",
                  color: colors.darkGray,
                }}
              >
                No meals found for {selectedDate}.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MealsSection;
